/*
 * @fileOverView Nav component definition
 */

import Vue from 'vue';

/**
 * A reference to the classes used in this view
 *
 * @property CLASSES
 * @type {Object}
 * @private
 */
const CLASSES = {
  'NO_SCROLL': 'noScroll',
  'NAV_FLYOUT_EXPANDED': 'nav-flyout_expanded'
};

/**
 * A reference to the intervals used in this view
 *
 * @property CLASSES
 * @type {Object}
 * @private
 */
const INTERVALS = {
  'FLYOUT': 300
}

/**
 * [Nav Vue]
 * @type {class}
 */
Vue.component('nav-vue', {
  data: function() {
    return {
      isExpanded: false,
      expandedPrimaryItem: null,
      overlayHeight: 'auto',
      searchHasValue: false,
      clearSearch: false,
      showDiv: false,
      focusableElements: [],
    }
  },

  watch: {
    isExpanded: function(value) {
      if (value) {
        document.body.classList.add(CLASSES.NO_SCROLL);
        this.$refs.flyout.classList.add(CLASSES.NAV_FLYOUT_EXPANDED);

        // Set focusable elements when nav is expanded.
        setTimeout(() => {
          this.setFocusableElements();
        }, 400);
      }
      else {
        document.body.classList.remove(CLASSES.NO_SCROLL);

        // Waits until the flyout is closed before returning the menu to its
        // original state.
        setTimeout(() => {
          this.expandedPrimaryItem = null;
          this.$refs.flyout.classList.remove(CLASSES.NAV_FLYOUT_EXPANDED);
        }, INTERVALS.FLYOUT);
      }
    },
    expandedPrimaryItem: function(newValue, oldValue) {
      // Update the overlay's height to match the active navigation's height.
      if (newValue !== null) {
        this.overlayHeight = this.$refs[`secondary${newValue}`].offsetHeight + 'px';
      }
    }
  },

  methods: {
    handleViewLibraryEvent() {
      this.showDiv = !this.showDiv; // Toggle the value to show/hide the div
    },

    handlePrimaryItemMouseEvent(value) {
      // These mouse events should only take effect on non-small breakpoints.
      if (window.getComputedStyle(this.$refs.flyout).position !== 'fixed') {
        this.expandedPrimaryItem = value;
      }
    },

    handlePrimaryLinkEvent(key, e) {
      if (e.type === 'touchstart') {
        // Prevents duplicate events (`mouseenter` and `touch`) from firing one
        // after the other on larger touch devices.
        e.preventDefault();
      }

      // Set focusable elements when nav is expanded.
      setTimeout(() => {
        this.setFocusableElements();
      }, 500);

      // Allows larger touch devices, keyboards to close an open primary item.
      this.expandedPrimaryItem = (this.expandedPrimaryItem !== key) ? key : null;

      if (this.expandedPrimaryItem !== null) {
        const firstSecondaryItem = this.$refs[`secondary${key}`].querySelector('.nav-secondary-back');

        if (firstSecondaryItem) {
          setTimeout(() => {
            firstSecondaryItem.focus();
          }, INTERVALS.FLYOUT);
        }
      }
    },

    /**
     * Handles closing the secondary menu and returns focus to the parent (primary) item.
     *
     * @method handleBackClick
     * @param {number} key - Identifier for the current primary item.
     * @param {Event} e - The event object.
     */
    handleBackClick(key, e) {
      e.preventDefault();

      // Set focusable elements when nav is expanded.
      setTimeout(() => {
        this.setFocusableElements();
      }, 500);

      // Collapse the expanded primary item.
      this.expandedPrimaryItem = null;

      // Return focus to the primary menu item.
      const parentPrimaryItem = this.$refs[`primaryItem${key}`];

      if (parentPrimaryItem) {
        setTimeout(() => {
          parentPrimaryItem.focus();
        }, INTERVALS.FLYOUT);
      }
    },

    /**
     * Handles when value is updated in the search form
     *
     * @method handleSearchValueChange
     * @param {string} value - The value of what is in the input field.
     * @public
     */
    handleSearchValueChange(value) {
      this.searchHasValue = Boolean(value);
      this.clearSearch = false;
    },

    /**
     * Handles click of the clear and close buttons
     *
     * @method handleClick
     * @public
     */
    handleClick() {
      if (this.searchHasValue) {
        this.clearSearch = true;
      } else {
        this.isExpanded = !this.isExpanded;
        this.$emit('nav-expanded', this.isExpanded);
      }
    },

    /**
     * Handles keyboard navigation for trapping focus inside the navigation.
     * Loops focus from the last element back to the first and vice versa.
     */
    handleKeydown(event) {
      const {key} = event;

      if (this.isExpanded) {
        if (key === 'Escape') {
          // Close the nav when Escape is pressed.
          this.isExpanded = false;
          return;
        }

        // Trap focus inside the navigation
        if (key === 'Tab') {
          const focusableLength = this.focusableElements.length;
          const focusedIndex = this.focusableElements.indexOf(document.activeElement);

          if (event.shiftKey && focusedIndex === 0) {
            // If Shift + Tab is pressed on the first focusable element, focus the last one
            event.preventDefault();
            this.focusableElements[focusableLength - 1].focus();
          } else if (!event.shiftKey && focusedIndex === focusableLength - 1) {
            // If Tab is pressed on the last focusable element, focus the first one
            event.preventDefault();
            this.focusableElements[0].focus();
          }
        }
      }
    },

    /**
     * Set focusable elements inside the navigation when expanded.
     * Focusable elements include links, buttons, form inputs, and the search input.
     */
    setFocusableElements() {
      this.focusableElements = [];

      const navigation = this.$refs.navigation;
      if (navigation) {
        this.focusableElements = Array.from(navigation.querySelectorAll('a, button, input, select, textarea, [tabindex]:not([tabindex="-1"])'))
            .filter(el => this.isElementVisible(el));
      }
    },

    /**
     * Check if an element is visible (i.e., not hidden by CSS).
     *
     * @param {HTMLElement} element - The element to check for visibility.
     * @returns {boolean} - Returns true if the element is visible, false if hidden.
     */
    isElementVisible(element) {
      const style = window.getComputedStyle(element);
      return (
          style.display !== 'none' &&
          style.visibility !== 'hidden' &&
          style.opacity !== '0'
      );
    },
  },
  mounted() {
    // Set up event listener for keydown
    window.addEventListener('keydown', this.handleKeydown);

    //bookmark menu dynamic positioning
    const utilityBtnsWrapper = document.querySelector('.nav-utility__btns');
    const libraryBtn = utilityBtnsWrapper.querySelector('.btn-library');

    if (libraryBtn) {
      libraryBtn.addEventListener("click", function() {
        var initialValue = 104;
        var spanWidth = libraryBtn.querySelector("span").offsetWidth;
        var difference = spanWidth - initialValue;
        var viewLibraryOptions = document.querySelector(".view-library-options");
        var viewportWidth = window.innerWidth || document.documentElement.clientWidth;

        if (viewportWidth > 1365) {
          var computedStyle = window.getComputedStyle(viewLibraryOptions);
          var currentLeftValue = parseInt(computedStyle.getPropertyValue("left")) || 0;
          var updatedLeftValue = currentLeftValue + difference;
          viewLibraryOptions.style.left = updatedLeftValue + "px";
        }
      });
    }
    //mobile navigation logic
    const navSecondaryExists = document.querySelector('.nav-primary .nav-primary__view-library-btn');
    // If the nav-secondary exists, hide the login list item
    if (navSecondaryExists) {
      const loginListItem = document.querySelector('.nav-primary-item.login-list-item');
      loginListItem.style.display = 'none';
    }
  },
});
